<template>
  <div class="list-container">
    <div class="list-header">
      <h1>Channels Admin</h1>
      <router-link to="/admin/channels/new" class="important-button big">
        <font-awesome-icon icon="plus" /> New Channel
      </router-link>
    </div>

    <table>
      <thead>
        <tr>
          <SortableListHeader
            column="name" 
            :current-sort="sortColumn" 
            default-direction="asc"
            @sort="handleSort"
          >
            Name
          </SortableListHeader>
          <SortableListHeader 
            column="organization" 
            :current-sort="sortColumn" 
            default-direction="asc"
            @sort="handleSort"
          >
            Organization
          </SortableListHeader>
          <SortableListHeader 
            column="videos" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Videos
          </SortableListHeader>
          <SortableListHeader 
            column="storage" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Storage
          </SortableListHeader>
          <SortableListHeader 
            column="created" 
            :current-sort="sortColumn" 
            @sort="handleSort"
          >
            Created
          </SortableListHeader>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr 
          v-for="channel in sortedChannels" 
          :key="channel.id" 
          @click="handleRowClick($event, channel)" 
          class="clickable"
          :title="`View &quot;${channel.name}&quot;`"
        >
          <td class="overflow">
            <b :title="channel.name">{{ channel.name }}</b>
          </td>
          <td>
            <router-link 
              v-if="channel.organization_id" 
              :to="`/organizations/${channel.organization_id}`" 
              :title="`View Organization &quot;${channel.organization_name}&quot;`"
              class="link-button"
            >
            {{ channel.organization_name }}
            </router-link>
          </td>
          <td>{{ channel.videoCount == null ? '-' : channel.videoCount }}</td>
          <td>{{ channel.storage == null ? '-' : formatFileSize(channel.storage) }}</td>
          <td :title="formatDatetime(channel.created_at)">{{ formatDate(channel.created_at) }}</td>
          <td>
            <span class="actions">
              <router-link
                :to="`/channels/${channel.id}/edit`"
                class="action-button edit"
                title="Edit"
              >
                <font-awesome-icon icon="edit" />
              </router-link>
              <button
                @click="confirmDelete(channel)"
                class="action-button delete"
                title="Delete"
              >
                <font-awesome-icon icon="trash" />
              </button>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { FontAwesomeIcon } from '@/scripts/icons';
import { getAllOrganizationsWithDetails, organizationKeys } from '@/scripts/api/organizationApi';
import { deleteChannel } from '@/scripts/api/channelApi';
import { handleApiError, isInteractiveElement, formatDate, formatDatetime, formatFileSize, showConfirmDialog } from '@/scripts/shared-utils';
import SortableListHeader from '@/components/SortableListHeader.vue';

const router = useRouter();
const queryClient = useQueryClient();
const sortColumn = ref('created');
const sortDirection = ref('desc');

// Fetch organizations and their nested channels
const { data: organizations } = useQuery({
  queryKey: organizationKeys.getAllOrganizationsWithDetails(),
  queryFn: getAllOrganizationsWithDetails,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'Error fetching organizations and channels'),
});

// Process the fetched organizations into a flat list of channels for the table
const allChannels = computed(() => {
  if (!organizations.value) return [];

  const processedChannels = [];
  organizations.value.forEach(org => {
    if (org.channels && Array.isArray(org.channels)) {
      org.channels.forEach(channel => {
        let videoCount = null;
        let storage = null;

        if (channel.video_files && Array.isArray(channel.video_files)) {
          videoCount = channel.video_files.length;
          storage = channel.video_files.reduce((acc, file) => acc + (Number(file.file_size) || 0), 0);
          if (storage === 0) storage = null;
        }

        processedChannels.push({
          ...channel,
          videoCount,
          storage,
          organization_name: org.name || '',
        });
      });
    }
  });
  return processedChannels;
});

// Sort the processed channels list
const sortedChannels = computed(() => {
  let sortedChannels = [...allChannels.value];
  
  sortedChannels.sort((a, b) => {
    let comparison = 0;
    let lhs, rhs;

    switch (sortColumn.value) {
      case 'name':
        lhs = a.name || '';
        rhs = b.name || '';
        comparison = lhs.localeCompare(rhs);
        break;
        
      case 'organization':
        lhs = a.organization_name || '';
        rhs = b.organization_name || '';
        comparison = lhs.localeCompare(rhs);
        break;
        
      case 'videos':
        lhs = a.videoCount || 0;
        rhs = b.videoCount || 0;
        comparison = lhs - rhs;
        break;
        
      case 'storage':
        lhs = a.storage || 0;
        rhs = b.storage || 0;
        comparison = lhs - rhs;
        break;
        
      case 'created':
      default:
        lhs = new Date(a.created_at || 0).getTime();
        rhs = new Date(b.created_at || 0).getTime();
        comparison = lhs - rhs;
        break;
    }
    
    return sortDirection.value === 'asc' ? comparison : -comparison;
  });
  
  return sortedChannels;
});

const handleSort = (sortData) => {
  sortColumn.value = sortData.column;
  sortDirection.value = sortData.direction;
};

// Mutation for deleting a channel
const deleteChannelMutation = useMutation({
  mutationFn: deleteChannel,
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: organizationKeys.getAllOrganizationsWithDetails() });
  },
  onError: (error) => {
    handleApiError(error, 'Error deleting channel');
  },
});

const confirmDelete = (channel) => {
  if (showConfirmDialog(`Are you sure you want to delete the channel "${channel.name}"?`)) {
    deleteChannelMutation.mutate(channel.id);
  }
};

const handleRowClick = (event, channel) => {
  if (!isInteractiveElement(event)) {
    router.push(`/channels/${channel.id}`);
  }
};
</script>

<style scoped>
@import '@/styles/shared-list.css';
</style>
